import React, { useEffect, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import { IconButton, Popover, Skeleton, Stack, Typography } from "@mui/material";
import { RoleComponentGuard } from "components/guards";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useFormatNumericHook } from "hooks";
import { formatCubicValue } from "utils";
import { pickBy } from "lodash";
import { useDealQueriesSelect, useReportsActions, useReportsQueriesSelector } from "store";
import { CubicData, DateRangeValue, ICubic } from "store/types";

import { RoleName } from "../../../constants";
import { KPIMenuContent } from "./KPIMenuContent";
import { StyledStack } from "./StyledStack";

// interface Props {}

const cubicsQuantity = 3;
const skeletons = new Array(cubicsQuantity).fill("");

interface MenuState {
  anchorEl: HTMLButtonElement;
  cubicKey: string;
  filter: string;
  title: string;
}

interface Props {
  internalDateRange?: DateRangeValue<string>;
}

export const KPIs: React.FC<Props> = ({ internalDateRange }) => {
  const { dateRange, exchangeMethod, currency, use_accounting_date } = useReportsQueriesSelector();
  const formatNumeric = useFormatNumericHook();
  const { filters, cubicInvestorView } = useDealQueriesSelect();

  const [menuState, setMenuState] = useState<MenuState | null>(null);

  const { getCubicAction } = useReportsActions();

  const dateRangeToUse = internalDateRange || dateRange;

  const {
    data: cubicData,
    action: getCubic,
    loading: cubicLoading //pageType: cubicSnapshot, cubicDeal
  } = useActionHook(getCubicAction);

  const handleCloseMenu = () => setMenuState(null);

  useEffect(() => {
    getCubic({
      cubics: Object.entries(cubicInvestorView)
        .slice(0, 3)
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {} as ICubic),
      pageType: "cubicInvestorView",
      filters: pickBy(filters, (value) => value.length > 0),
      exchangeMethod,
      currency,
      dateRange: dateRangeToUse,
      use_accounting_date
    });
  }, [filters, exchangeMethod, currency, dateRangeToUse, cubicInvestorView, use_accounting_date]);

  return (
    <StyledStack sx={{ flexGrow: 1 }}>
      <Typography sx={{ fontSize: "18px", fontWeight: 500, lineHeight: "24px", color: "#071F35" }}>Overview</Typography>
      <Stack sx={{ flexDirection: "row", gap: "10px", alignItems: "stretch", flexGrow: 1 }}>
        {cubicLoading || !cubicData
          ? skeletons.map((_, index) => <Skeleton key={index} variant="rounded" sx={{ height: "100%", flexGrow: 1 }} />)
          : Object.entries(cubicData).map(([key, item]: [string, CubicData]) => (
              <Stack
                key={key}
                sx={{
                  p: "8px 10px",
                  border: "1px solid #DBE8F5",
                  flexGrow: 1,
                  flex: "33%",
                  justifyContent: "center",
                  position: "relative",
                  ".MuiIconButton-root": {
                    opacity: 0
                  },
                  ":hover": {
                    ".MuiIconButton-root": {
                      opacity: 1
                    }
                  }
                }}
              >
                {Boolean(item.pme_warning) && (
                  <MaterialTooltip isShow title={item.pme_warning} centerText placement="top">
                    <InfoOutlinedIcon
                      fontSize="sm"
                      sx={{ fill: "#071F35", position: "absolute", top: "22px", left: "10px" }}
                    />
                  </MaterialTooltip>
                )}
                <Typography sx={{ textAlign: "center", fontSize: "24px", color: "#496682", fontWeight: 400 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ textAlign: "center", fontSize: "24px", color: "#071F35", fontWeight: 700 }}>
                  {formatCubicValue(item, formatNumeric)}
                </Typography>
                <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                  <IconButton
                    onClick={(e) => {
                      setMenuState({
                        cubicKey: key,
                        anchorEl: e.currentTarget,
                        title: item.title,
                        filter: item.filter
                      });
                    }}
                    sx={{
                      position: "absolute",
                      right: "2px",
                      top: "2px",
                      zIndex: 100,
                      transition: "opacity .15s linear"
                    }}
                  >
                    <TuneIcon sx={{ height: 20, width: 20, color: "#5d7782" }} />
                  </IconButton>
                </RoleComponentGuard>
              </Stack>
            ))}
      </Stack>
      <Popover anchorEl={menuState?.anchorEl} open={Boolean(menuState?.anchorEl)} onClose={handleCloseMenu}>
        <Stack
          sx={{
            bgcolor: "white",
            p: "15px 10px",
            boxShadow: "sm",
            borderRadius: 5,
            gap: "15px",
            maxWidth: 250,
            minWidth: 250,
            minHeight: 170
          }}
        >
          {menuState && <KPIMenuContent {...menuState} handleClose={handleCloseMenu} />}
        </Stack>
      </Popover>
    </StyledStack>
  );
};
